/**
 * Panel (Dashboard) — backend besleme + mock yardımcı veriler.
 *
 * Gerçek backend çağrıları:
 *   - appState.backend.listDevices() → toplam + çevrimiçi sayısı
 *   - appState.backend.getAddressBook() → paylaşılan erişim sayısı
 *
 * Backend'den henüz çekilmeyenler (tenant-scope admin endpoint'leri user'a
 * açık değil): bugünkü bağlantı sayısı, veri transferi GB, haftalık trafik.
 * Bunlar lokal mock — gerçek metrik backend'e eklendiğinde swap edilir.
 */
import { appState } from '../state';
import { applyStyle, escapeHtml } from '../ui/css';
import { createCard } from '../ui/components/card';
import { createPageHeader } from '../ui/components/pageHeader';
import { createTagBadge } from '../ui/components/badge';
export function renderDashboard() {
    const root = document.createElement('div');
    // Header — saate göre dinamik selamlama + tarih/saat
    root.append(createPageHeader({
        title: greetingFor(new Date()).title,
        highlight: greetingFor(new Date()).highlight + (appState.userEmail
            ? ', ' + capitalize(appState.userEmail.split('@')[0])
            : ''),
        subtitle: 'Bağlantı istatistikleri, son aktivite ve sistem durumu.',
        rightSlot: dateBadge(new Date()),
    }));
    // Metric grid — 4 kart
    const grid = document.createElement('div');
    grid.classList.add('pr-grid-cards');
    applyStyle(grid, {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 'var(--sp-3)',
        marginBottom: 'var(--sp-4)',
    });
    const m1 = metricCard({ accent: 'var(--accent)', label: 'AKTİF OTURUMLAR', value: '—', meta: 'yükleniyor…' });
    const m2 = metricCard({ accent: 'var(--status-info)', label: 'TOPLAM CİHAZ', value: '—', meta: 'yükleniyor…' });
    const m3 = metricCard({ accent: 'var(--status-busy)', label: 'PAYLAŞILAN ERİŞİM', value: '—', meta: 'yükleniyor…' });
    const m4 = metricCard({ accent: '#a78bfa', label: 'BUGÜNKÜ BAĞLANTI', value: '14', meta: 'son 24 saat' });
    grid.append(m1.el, m2.el, m3.el, m4.el);
    root.append(grid);
    // 2-kolon: Son aktivite + sistem durumu
    const row2 = document.createElement('div');
    row2.classList.add('pr-dash-grid');
    applyStyle(row2, {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)',
        gap: 'var(--sp-3)',
        marginBottom: 'var(--sp-4)',
    });
    const activityCard = createCard({
        caption: 'SON 24 SAAT',
        title: 'Son Aktivite',
        headerRight: makeLink('Tümünü Gör →', () => appState.navigate('contacts')),
    });
    const activityList = document.createElement('div');
    applyStyle(activityList, { display: 'flex', flexDirection: 'column', marginTop: 'var(--sp-3)' });
    const loadingAct = document.createElement('div');
    loadingAct.textContent = 'yükleniyor…';
    applyStyle(loadingAct, { color: 'var(--text-tertiary)', fontSize: 'var(--fs-caption)', padding: 'var(--sp-3)' });
    activityList.append(loadingAct);
    activityCard.append(activityList);
    row2.append(activityCard);
    const healthCard = createCard({
        caption: 'REALTIME',
        title: 'Sistem Durumu',
    });
    const healthBody = document.createElement('div');
    applyStyle(healthBody, { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', marginTop: 'var(--sp-3)' });
    healthBody.innerHTML = `
    ${healthRow('Relay sunucusu', 'EU-WEST · 28ms', 'online')}
    ${healthRow('Kimlik doğrulama', '2FA aktif', 'online')}
    ${healthRow('Şifreleme', 'AES-256 · E2E', 'online')}
    ${healthRow('Yedekleme', '6 sa önce', 'busy')}
    ${healthRow('Lisans', 'Pro · 38 gün', 'online')}
  `;
    healthCard.append(healthBody);
    row2.append(healthCard);
    root.append(row2);
    // Haftalık chart kartı
    const chartCard = createCard({
        caption: 'SON 7 GÜN',
        title: 'Haftalık Bağlantı Trafiği',
    });
    const chart = document.createElement('div');
    applyStyle(chart, {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 'var(--sp-2)',
        height: '160px',
        padding: 'var(--sp-3) 0',
    });
    const days = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Pzr'];
    const heights = [45, 70, 55, 88, 62, 30, 78];
    days.forEach((d, i) => {
        const col = document.createElement('div');
        applyStyle(col, { flex: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%' });
        const fill = document.createElement('div');
        applyStyle(fill, {
            width: '100%',
            height: `${heights[i]}%`,
            background: i === days.length - 1
                ? 'var(--accent)'
                : 'linear-gradient(180deg, var(--accent) 0%, rgba(163, 230, 53, 0.4) 100%)',
            borderRadius: '4px 4px 0 0',
            transition: 'opacity var(--motion-fast)',
        });
        col.append(fill);
        const lbl = document.createElement('span');
        lbl.textContent = d;
        applyStyle(lbl, {
            fontSize: 'var(--fs-tiny)',
            color: i === days.length - 1 ? 'var(--accent)' : 'var(--text-tertiary)',
            fontFamily: 'var(--font-mono)',
        });
        col.append(lbl);
        chart.append(col);
    });
    chartCard.append(chart);
    root.append(chartCard);
    // ── Backend çağrıları ──
    (async () => {
        try {
            const devices = await appState.backend.listDevices();
            const hosts = devices.filter(d => !d.displayName.startsWith('PhotonRemote Web'));
            m2.update({
                value: String(hosts.length).padStart(2, '0'),
                meta: `${hosts.filter(d => !d.revokedAt).length} aktif`,
            });
            // Aktif oturumları presence ile yokla (paralel sorgu)
            const presences = await Promise.all(hosts.map(async (d) => {
                try {
                    const r = await fetch(`${appState.backendUrl}/devices/${d.deviceId}/presence`, {
                        headers: { 'Authorization': `Bearer ${appState.accessToken ?? ''}` },
                    });
                    if (!r.ok)
                        return null;
                    return await r.json();
                }
                catch {
                    return null;
                }
            }));
            const sharing = presences.filter(p => p?.status === 'sharing').length;
            const online = presences.filter(p => p?.status === 'online' || p?.status === 'sharing').length;
            m1.update({
                value: String(sharing).padStart(2, '0'),
                meta: `${online} cihaz çevrimiçi`,
            });
        }
        catch {
            m1.update({ value: '00', meta: '— hata' });
            m2.update({ value: '—', meta: '— hata' });
        }
    })();
    (async () => {
        try {
            const ab = await appState.backend.getAddressBook();
            const shared = ab.filter(e => e.kind !== 0).length;
            m3.update({
                value: String(shared).padStart(2, '0'),
                meta: `${ab.length} adres defteri toplam`,
            });
            // Son aktivite — adres defterindeki en son kullanılanlar
            activityList.innerHTML = '';
            const recent = [...ab]
                .sort((a, b) => (b.lastUsedAt ?? b.createdAt).localeCompare(a.lastUsedAt ?? a.createdAt))
                .slice(0, 6);
            if (recent.length === 0) {
                const empty = document.createElement('div');
                empty.textContent = 'Henüz aktivite yok — bir cihaza bağlanınca burada görünür.';
                applyStyle(empty, { color: 'var(--text-tertiary)', fontSize: 'var(--fs-caption)', padding: 'var(--sp-3)' });
                activityList.append(empty);
                return;
            }
            for (const e of recent) {
                activityList.append(activityRow({
                    time: relTime(e.lastUsedAt ?? e.createdAt),
                    title: e.nickname || e.displayName || 'İsimsiz',
                    meta: e.targetHumanId ? `ID: ${e.targetHumanId}` : '(bilgi yok)',
                    status: e.kind === 0 ? 'success' : (e.kind === 1 ? 'info' : 'busy'),
                    statusText: e.kind === 0 ? 'KENDİ' : (e.kind === 1 ? 'PAYLAŞIM' : 'MANUEL'),
                }));
            }
        }
        catch {
            activityList.innerHTML = '';
            const err = document.createElement('div');
            err.textContent = 'Aktivite yüklenemedi (yetki/bağlantı)';
            applyStyle(err, { color: 'var(--status-error)', fontSize: 'var(--fs-caption)', padding: 'var(--sp-3)' });
            activityList.append(err);
            m3.update({ value: '—', meta: '— hata' });
        }
    })();
    return root;
}
function metricCard(opts) {
    const wrap = document.createElement('div');
    applyStyle(wrap, {
        background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-4)',
        position: 'relative',
        overflow: 'hidden',
    });
    const indicator = document.createElement('div');
    applyStyle(indicator, {
        position: 'absolute', top: '0', left: '0',
        width: '3px', height: '100%',
        background: opts.accent,
    });
    wrap.append(indicator);
    const label = document.createElement('div');
    label.textContent = opts.label;
    applyStyle(label, {
        fontSize: 'var(--fs-tiny)',
        letterSpacing: '0.12em',
        color: 'var(--text-tertiary)',
        fontWeight: '600',
        marginBottom: 'var(--sp-2)',
    });
    wrap.append(label);
    const valueEl = document.createElement('div');
    valueEl.textContent = opts.value;
    applyStyle(valueEl, {
        fontFamily: 'var(--font-display)',
        fontStyle: 'italic',
        fontSize: '36px',
        fontWeight: '500',
        lineHeight: '1',
        color: 'var(--text-primary)',
        marginBottom: 'var(--sp-2)',
    });
    wrap.append(valueEl);
    const metaEl = document.createElement('div');
    metaEl.textContent = opts.meta;
    applyStyle(metaEl, {
        fontSize: 'var(--fs-caption)',
        color: 'var(--text-secondary)',
    });
    wrap.append(metaEl);
    return {
        el: wrap,
        update: ({ value, meta }) => {
            valueEl.textContent = value;
            metaEl.textContent = meta;
        },
    };
}
function activityRow(opts) {
    const row = document.createElement('div');
    applyStyle(row, {
        display: 'grid',
        gridTemplateColumns: '60px 1fr auto',
        gap: 'var(--sp-3)',
        alignItems: 'center',
        padding: 'var(--sp-2) 0',
        borderBottom: '1px solid var(--border-subtle)',
    });
    const time = document.createElement('span');
    time.textContent = opts.time;
    applyStyle(time, {
        fontSize: 'var(--fs-tiny)',
        fontFamily: 'var(--font-mono)',
        color: 'var(--text-tertiary)',
    });
    row.append(time);
    const meta = document.createElement('div');
    meta.innerHTML = `
    <div style="font-size:var(--fs-caption);font-weight:600;color:var(--text-primary)">${escapeHtml(opts.title)}</div>
    <div style="font-size:var(--fs-tiny);color:var(--text-tertiary);margin-top:2px">${escapeHtml(opts.meta)}</div>
  `;
    row.append(meta);
    row.append(createTagBadge(opts.statusText, opts.status));
    return row;
}
function healthRow(name, value, status) {
    const colors = {
        online: 'var(--accent)',
        busy: 'var(--status-busy)',
        error: 'var(--status-error)',
    };
    return `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:var(--sp-3);padding:var(--sp-2) 0;border-bottom:1px solid var(--border-subtle)">
      <div style="display:flex;align-items:center;gap:var(--sp-2)">
        <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${colors[status]};box-shadow:0 0 0 3px ${colors[status]}33;flex-shrink:0"></span>
        <span style="font-size:var(--fs-caption);color:var(--text-primary)">${escapeHtml(name)}</span>
      </div>
      <span style="font-family:var(--font-mono);font-size:var(--fs-tiny);color:var(--text-secondary)">${escapeHtml(value)}</span>
    </div>
  `;
}
function makeLink(text, onClick) {
    const a = document.createElement('button');
    a.type = 'button';
    a.textContent = text;
    applyStyle(a, {
        background: 'transparent', border: 'none',
        color: 'var(--accent)',
        fontSize: 'var(--fs-caption)', fontWeight: '500',
        cursor: 'pointer',
        fontFamily: 'inherit',
    });
    a.addEventListener('click', onClick);
    return a;
}
function greetingFor(d) {
    const hour = d.getHours();
    let greet = 'Merhaba';
    if (hour < 6)
        greet = 'İyi geceler';
    else if (hour < 11)
        greet = 'Günaydın';
    else if (hour < 14)
        greet = 'İyi öğlenler';
    else if (hour < 18)
        greet = 'İyi günler';
    else
        greet = 'İyi akşamlar';
    return { title: greet, highlight: '' };
}
function capitalize(s) {
    return s ? s[0].toUpperCase() + s.slice(1) : '';
}
function dateBadge(d) {
    const div = document.createElement('div');
    applyStyle(div, {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '2px',
        fontSize: 'var(--fs-caption)',
        color: 'var(--text-tertiary)',
        letterSpacing: '0.06em',
    });
    const days = ['PAZAR', 'PAZARTESİ', 'SALI', 'ÇARŞAMBA', 'PERŞEMBE', 'CUMA', 'CUMARTESİ'];
    const months = ['OCAK', 'ŞUBAT', 'MART', 'NİSAN', 'MAYIS', 'HAZİRAN', 'TEMMUZ', 'AĞUSTOS', 'EYLÜL', 'EKİM', 'KASIM', 'ARALIK'];
    div.innerHTML = `
    <span>${days[d.getDay()]} · ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}</span>
    <span style="font-family:var(--font-mono);font-size:var(--fs-h3);color:var(--text-secondary);letter-spacing:0.08em">
      ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}
    </span>
  `;
    return div;
}
function relTime(iso) {
    if (!iso)
        return '—';
    const t = Date.parse(iso);
    if (Number.isNaN(t))
        return '—';
    const diff = Date.now() - t;
    const m = Math.floor(diff / 60000);
    if (m < 1)
        return 'şimdi';
    if (m < 60)
        return `${m} dk`;
    const h = Math.floor(m / 60);
    if (h < 24)
        return `${h} sa`;
    const d = Math.floor(h / 24);
    return `${d} gün`;
}
